import { computed, ref } from 'vue';

/** 项目维度：WBS 生成进行中时锁定侧栏/详情编辑，避免生成结果覆盖用户改动 */
const generatingProjectIds = ref<Set<number>>(new Set());

/** 同一项目仅保留一条进行中的 generate Promise，避免路由/生命周期重复触发排队 */
const inFlightByProjectId = new Map<number, Promise<unknown>>();

export function setProjectWbsGenerateLock(projectId: number, locked: boolean) {
  const id = Number(projectId);
  if (!Number.isFinite(id) || id <= 0) return;
  const next = new Set(generatingProjectIds.value);
  if (locked) next.add(id);
  else next.delete(id);
  generatingProjectIds.value = next;
}

export function isProjectWbsGenerateLocked(projectId: number | null | undefined): boolean {
  const id = Number(projectId);
  if (!Number.isFinite(id) || id <= 0) return false;
  return generatingProjectIds.value.has(id);
}

export function getProjectWbsGenerateInFlight(projectId: number | null | undefined): Promise<unknown> | undefined {
  const id = Number(projectId);
  if (!Number.isFinite(id) || id <= 0) return undefined;
  return inFlightByProjectId.get(id);
}

/**
 * 原子占位：同一 projectId 只允许一条进行中的生成。
 * 若已有任务，返回已有 Promise；否则执行 factory 并登记。
 */
export function runExclusiveProjectWbsGenerate<T>(
  projectId: number,
  factory: () => Promise<T>,
): Promise<T> {
  const id = Number(projectId);
  if (!Number.isFinite(id) || id <= 0) {
    return factory();
  }
  const existing = inFlightByProjectId.get(id);
  if (existing) {
    return existing as Promise<T>;
  }

  let settle!: (value: T | PromiseLike<T>) => void;
  let fail!: (reason?: unknown) => void;
  const placeholder = new Promise<T>((resolve, reject) => {
    settle = resolve;
    fail = reject;
  });
  inFlightByProjectId.set(id, placeholder);
  setProjectWbsGenerateLock(id, true);

  try {
    const real = factory();
    real.then(settle, fail);
  } catch (e) {
    fail(e);
  }

  const tracked = placeholder.finally(() => {
    if (inFlightByProjectId.get(id) === tracked) {
      inFlightByProjectId.delete(id);
    }
    setProjectWbsGenerateLock(id, false);
  });
  inFlightByProjectId.set(id, tracked);
  return tracked;
}

/** @deprecated 使用 runExclusiveProjectWbsGenerate */
export function trackProjectWbsGenerateInFlight<T>(
  projectId: number,
  promise: Promise<T>,
): Promise<T> {
  return runExclusiveProjectWbsGenerate(projectId, () => promise);
}

export function useProjectWbsGenerateLock(projectId: () => number | null | undefined) {
  return computed(() => isProjectWbsGenerateLocked(projectId()));
}
